import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { CheckBoxUtils } from "../../../UI.ElementarControls/Helpers/CheckBoxUtils";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { UIManager } from "../UIManager";
import { ControlParams, IMultiEdit } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export interface CheckBoxInput extends HTMLInputElement {
    tscValue: boolean;
}

export class CheckBox extends EditableControl {
    public override innerElement: CheckBoxInput;
    protected _listenerClick: EventListener;
    protected _listenerFocus: EventListener;
    protected _listenerBlur: EventListener;
    private readonly _label: HTMLLabelElement;

    private _displayFormat3S = true;

    public set displayFormat(format: string) {
        this._displayFormat3S = format == null || format.toLowerCase() !== "2s";
    }

    /**
     * true = 3S, false = 2S
     */
    public get displayFormat3S(): boolean {
        return this._displayFormat3S;
    }

    public override get displayedValue(): ITextBuilder {
        return this.innerElement.tscValue;
    }

    public override set displayedValue(v: ITextBuilder) {
        if (typeof v === "boolean")
            this.innerElement.tscValue = v;
    }

    public override get isVisuallyEncrypted(): boolean {
        return this._isVisuallyEncrypted;
    }

    public override set isVisuallyEncrypted(val: boolean) {
        if (this._isVisuallyEncrypted === val)
            return;

        this._isVisuallyEncrypted = val;
        this.element.classList.toggle("encrypted", val);
        this._label.classList.toggle("encrypted-symbol", val);
        this._label.classList.toggle("icon", val);
        this._label.classList.toggle("user-lock", val);
    }

    constructor(params: ControlParams) {
        super(params);
        const { element } = params;

        element.classList.remove("EditableControl");
        element.classList.add("CheckBox");

        this.displayFormat = element.getAttribute("data-displayformat");

        this.innerElement = this._createCheckBox();

        this.element.appendChild(this.innerElement);

        const locKey = element.getAttribute("data-captionLocKey");
        this._label = document.createElement("label");
        this._label.htmlFor = this.innerElement.id;

        if (locKey) {
            Localization.addLocData(this._label, locKey, Localization.TEXT_CONTENT, "title");
            this._label.classList.add("Caption");
        }

        this.element.appendChild(this._label);

        /*
        this.InnerElement.addEventListener('change', () => {
            this.ValueChanged.trigger(this, { Sender: this });
        });
        */ //vola se dvakrat za sebou :-(
    }

    /**
     * Kontrola jestli je vyplnene povinne pole
     */
    public override isFilledRequiredControl(): boolean {
        const isFilledRequiredControl = !(this.required && this.controlValue == null);

        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || isFilledRequiredControl;
        }

        return isFilledRequiredControl;
    }

    /**
     * Kontrola jestli je vyplnene doporucene pole
     */
    public override isFilledRecommendedControl(): boolean {
        const isFilledRecommendedControl = !(this.recommended && this.controlValue == null);

        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || isFilledRecommendedControl;
        }

        return isFilledRecommendedControl;
    }

    /**
     * Kontrola jestli je vyplnene postponed pole
     */
    public override isFilledPostponedControl(): boolean {
        const isFilledPostponedControl = !(this.postponedRequired && this.controlValue == null && this.visible);

        if (this instanceof IMultiEdit && this.multiEditActive) {
            return !this.touched || isFilledPostponedControl;
        }

        return isFilledPostponedControl;
    }

    protected _createCheckBox(): CheckBoxInput {
        const checkBox = createElement({ type: "input", attrs: { "type": "checkbox" } }) as CheckBoxInput;

        checkBox.type = "checkbox";
        checkBox.indeterminate = true;
        checkBox.id = UIManager.instance.generateElementID();

        Object.defineProperty(checkBox, "tscValue", {
            get: function() {
                return !(this as Element).getAttribute("value") ? null : (this as Element).getAttribute("value") === "true"; /* atribut value má vždy textovou hodnotu */
            },
            set: function(value: boolean | string | number) {
                const t: CheckBoxInput = this as CheckBoxInput;
                CheckBoxUtils.setCheckValue(t, value);
            },
        });

        checkBox.tscValue = DataConverter.toBoolean(this.element.getAttribute("value"), null); //pocatecni hodnota checkboxu

        checkBox.addEventListener("click", this._listenerClick = (e: MouseEvent) => {
            EventHelper.stopPropagation(e);

            if (e.ctrlKey || e.metaKey) // Zobrazení informací o komponentě
                return;

            const t = e.target as CheckBoxInput;
            const owner = (t.parentNode as any).tscControl as CheckBox;

            if ((!owner || owner.isEditing) && this._isActive) {
                switch (t.tscValue) {
                    case false:
                        if (this.required || !this.displayFormat3S)
                            t.tscValue = true;
                        else
                            t.tscValue = null;
                        break;

                    // checked na unchecked
                    case true:
                        t.tscValue = false;
                        break;

                    // indeterminate na checked
                    default:
                        t.tscValue = true;
                }

                // pokud bychom chteli vyvolavat i klasicky HTML event change
                /*
                var evt = document.createEvent("HTMLEvents");
                evt.initEvent("change", true, true);
                t.dispatchEvent(evt);
                */

                if (owner)
                    owner.valueChanged.trigger(owner, t.tscValue);
            }
        });
        checkBox.addEventListener("focus", this._listenerFocus = (e: FocusEvent) => {
            this.getFocus.trigger(this, e);
            this.element.classList.add("focused");
        });
        checkBox.addEventListener("blur", this._listenerBlur = (e: FocusEvent) => {
            this.lostFocus.trigger(this, e);
            this.element.classList.remove("focused");
        });
        return checkBox;
    }

    /**
     * Vrati hodnotu editu
     */
    public override get controlValue(): boolean {
        return this.innerElement.tscValue;
    }

    /**
     * Nastavi hodnotu editu
     */
    public override set controlValue(value: boolean) {
        this.innerElement.tscValue = value;
    }

    public override startEdit(): void {
        this.innerElement.click();
    }

    protected override _editableControlClicked(_sender: EditableControl, _event: MouseEvent): void {
        // Focus checkbox after clicking on EditableControl.
        this.innerElement.focus();
    }

    //destruktor
    public override dispose(): void {
        const checkBox = document.createElement("input") as CheckBoxInput;
        checkBox.removeEventListener("click", this._listenerClick);
        checkBox.removeEventListener("focus", this._listenerFocus);
        checkBox.removeEventListener("blur", this._listenerBlur);

        this.innerElement = null;
        super.dispose();
    }
}
